import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import SEO from "../components/common/SEO";
import {
  Check,
  X,
  Globe,
  Zap,
  Shield,
  ShieldCheck,
  HelpCircle,
  ChevronDown,
  Coins,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
const APP_NAME = "VALIDMAIL";

const CURRENCY_RATES = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  INR: 83.5,
  AUD: 1.52,
  CAD: 1.36,
};

const CURRENCY_LABELS = {
  USD: "🇺🇸 USD - US Dollar",
  EUR: "🇪🇺 EUR - Euro",
  GBP: "🇬🇧 GBP - British Pound",
  INR: "🇮🇳 INR - Indian Rupee",
  AUD: "🇦🇺 AUD - Australian Dollar",
  CAD: "🇨🇦 CAD - Canadian Dollar",
};

const COUNTRY_TO_CURRENCY = {
  US: "USD",
  CA: "CAD",
  GB: "GBP",
  UK: "GBP",
  IN: "INR",
  AU: "AUD",
  NZ: "AUD",
  SG: "USD",
  DE: "EUR",
  FR: "EUR",
  ES: "EUR",
  IT: "EUR",
  NL: "EUR",
  BE: "EUR",
  DK: "EUR",
  NO: "EUR",
  SE: "EUR",
  FI: "EUR",
  IE: "EUR",
};

const DEFAULT_CURRENCY = "USD";

const getDefaultCurrency = () => {
  if (typeof navigator !== "undefined") {
    const locale = navigator.language || "en-US";
    const region = locale.split("-")[1];
    if (region && COUNTRY_TO_CURRENCY[region.toUpperCase()]) {
      return COUNTRY_TO_CURRENCY[region.toUpperCase()];
    }
  }
  return DEFAULT_CURRENCY;
};

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const FAQS = [
  {
    question: "How do validation credits work?",
    answer:
      "Each email validation (real-time or bulk) uses 1 credit. Credits refresh monthly and never expire within your subscription period. Unused credits don't roll over between billing cycles.",
  },
  {
    question: "Can I change plans anytime?",
    answer:
      "Yes! You can upgrade or downgrade your plan at any time. Upgrades take effect immediately, while downgrades apply at the start of your next billing cycle.",
  },
  {
    question: "What happens if I exceed my credit limit?",
    answer:
      "You can purchase additional credit packs as needed, or upgrade to a higher tier. We'll notify you when you reach 80% of your limit so you never run out unexpectedly.",
  },
  {
    question: "Do you offer refunds?",
    answer:
      "We offer a 30-day money-back guarantee on all paid plans. If you're not satisfied for any reason, contact us for a full refund—no questions asked.",
  },
  {
    question: "Can I try before I buy?",
    answer:
      "Absolutely! Our Starter plan includes 100 free credits every month. No credit card required. Test our validation accuracy and see results before committing to a paid plan.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit cards (Visa, Mastercard, Amex), PayPal, and bank transfers for Enterprise plans. All payments are processed securely through industry-standard encryption.",
  },
];

export default function Pricing({ user, authToken, onAuthRequired, onUserUpdate }) {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [pricingCurrency, setPricingCurrency] = useState(getDefaultCurrency());
  const [pricingLoading, setPricingLoading] = useState(false);
  const [defaultCredits, setDefaultCredits] = useState(0);
  const [activePurchasePlan, setActivePurchasePlan] = useState("");
  const [statusMessage, setStatusMessage] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(0);

  const isAuthenticated = Boolean(authToken);

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    [authToken]
  );

  const currencyOptions = useMemo(
    () =>
      Object.keys(CURRENCY_RATES).map((code) => ({
        code,
        label: CURRENCY_LABELS[code],
      })),
    []
  );

  const convertPrice = (usd) => {
    const rate = CURRENCY_RATES[pricingCurrency] || 1;
    return usd * rate;
  };

  const formatPrice = (usd) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: pricingCurrency || DEFAULT_CURRENCY,
      }).format(convertPrice(usd));
    } catch (error) {
      return `${convertPrice(usd).toFixed(2)} ${pricingCurrency}`;
    }
  };

  const handleCurrencyChange = (event) => {
    setPricingCurrency(event.target.value);
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? -1 : index);
  };

  const handleApiError = (error, fallbackMessage) => {
    console.error(fallbackMessage, error);
    const status = error?.response?.status;
    if (status === 401) {
      return "Your session expired. Please sign in again.";
    }
    if (status === 402) {
      return "Insufficient credits. Purchase a plan to continue.";
    }
    return error?.response?.data?.detail || fallbackMessage;
  };

  const handlePurchasePlan = async (planId) => {
    if (!isAuthenticated) {
      const message = "Sign in to purchase credits.";
      setStatusMessage({ type: "error", text: message });
      if (onAuthRequired) onAuthRequired();
      return;
    }

    const plan = pricingPlans.find((p) => p.plan_id === planId);
    if (!plan) {
      alert("Invalid plan!");
      return;
    }

    setPricingLoading(true);
    setActivePurchasePlan(planId);
    setStatusMessage(null);

    const sdkLoaded = await loadRazorpayScript();
    if (!sdkLoaded) {
      setStatusMessage({
        type: "error",
        text: "Failed to load payment SDK. Try again.",
      });
      setPricingLoading(false);
      setActivePurchasePlan("");
      return;
    }

    try {
      const createResp = await axios.post(
        `${API}/credits/create-order`,
        { plan_id: planId },
        { headers: authHeaders }
      );

      const { order, razorpay_key_id } = createResp.data;
      if (!order || !order.id || typeof order.amount === "undefined") {
        throw new Error("Invalid order response from server");
      }

      const options = {
        key: razorpay_key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: APP_NAME,
        description: `Purchase ${plan.name} Plan`,
        order_id: order.id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        handler: async function (response) {
          try {
            const verifyResp = await axios.post(
              `${API}/credits/verify`,
              {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                plan_id: planId,
              },
              { headers: authHeaders }
            );

            if (onUserUpdate) onUserUpdate(verifyResp.data);
            setStatusMessage({
              type: "success",
              text: `Payment successful! ${plan.credits.toLocaleString()} credits added. New balance: ${
                verifyResp.data.credits
              }.`,
            });
          } catch (err) {
            const message = handleApiError(err, "Unable to verify payment.");
            setStatusMessage({ type: "error", text: message });
          } finally {
            setPricingLoading(false);
            setActivePurchasePlan("");
          }
        },
        modal: {
          ondismiss: function () {
            setPricingLoading(false);
            setActivePurchasePlan("");
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (resp) {
        const errorMsg =
          (resp && resp.error && resp.error.description) || "Payment failed";
        setStatusMessage({ type: "error", text: `Payment failed: ${errorMsg}` });
        setPricingLoading(false);
        setActivePurchasePlan("");
      });

      rzp.open();
    } catch (error) {
      console.error("Purchase error:", error);
      const message = handleApiError(error, "Unable to create order.");
      setStatusMessage({ type: "error", text: message });
      setPricingLoading(false);
      setActivePurchasePlan("");
    }
  };

  useEffect(() => {
    const loadPricing = async () => {
      setPricingLoading(true);
      try {
        const response = await axios.get(`${API}/pricing`);
        setPricingPlans(response.data?.plans || []);
        setDefaultCredits(response.data?.default_free_credits || 0);
      } catch (error) {
        setStatusMessage({
          type: "error",
          text: "Unable to load pricing at the moment.",
        });
      } finally {
        setPricingLoading(false);
      }
    };

    loadPricing();
  }, []);

  return (
    <>
      <SEO
        title="Pricing - VALIDMAIL | Transparent Email Validation Plans"
        description="Simple, transparent pricing for email validation. Start free with 100 credits. Scale with pay-as-you-go plans. No hidden fees, cancel anytime."
        keywords="email validation pricing, email verification cost, email validation plans, affordable email validation, free email checker"
        canonicalUrl="https://validmail.io/pricing"
        type="website"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-6">
              Flexible pricing{" "}
              <span className="text-sky-600 dark:text-sky-400">
                for reliable outreach
              </span>
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-8">
              Pay only for what you use. Credits never expire. No hidden fees.
            </p>

            {/* Currency Selector */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <Globe className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              <select
                value={pricingCurrency}
                onChange={handleCurrencyChange}
                className="px-4 py-2 rounded-full border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 font-medium focus:outline-none focus:border-sky-500 dark:focus:border-sky-400"
              >
                {currencyOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Prices shown in {pricingCurrency}. Adjust to match your market.
            </p>
          </div>

          {/* Status Message */}
          {statusMessage && (
            <div
              className={`mb-8 rounded-xl border px-6 py-4 text-center ${
                statusMessage.type === "error"
                  ? "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400"
              }`}
            >
              {statusMessage.text}
            </div>
          )}

          {/* Pricing Cards */}
          {pricingLoading && !pricingPlans.length ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 dark:text-slate-400">
              <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-sky-500 border-t-transparent" />
              <p>Loading pricing plans...</p>
            </div>
          ) : pricingPlans.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {pricingPlans.map((plan) => {
                const isPopular = plan.plan_id === "growth";
                return (
                  <div
                    key={plan.plan_id}
                    className={`relative rounded-3xl p-8 border-2 transition-all hover:shadow-2xl hover:-translate-y-2 ${
                      isPopular
                        ? "border-sky-400 dark:border-sky-600 bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 shadow-xl scale-105"
                        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-sky-600 to-blue-600 text-white text-sm font-bold rounded-full shadow-lg">
                        Most Popular
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-2">
                        {plan.name}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {plan.description}
                      </p>
                    </div>

                    <div className="mb-6">
                      <div className="text-4xl font-bold text-slate-900 dark:text-slate-50 mb-2">
                        {formatPrice(plan.price_usd)}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        ≈ ${Number(plan.price_usd || 0).toFixed(2)} USD
                      </div>
                      <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        <Coins className="h-4 w-4" />
                        {Number(plan.credits || 0).toLocaleString()} credits
                      </div>
                    </div>

                    <button
                      onClick={() => handlePurchasePlan(plan.plan_id)}
                      disabled={
                        pricingLoading && activePurchasePlan === plan.plan_id
                      }
                      className={`w-full py-3 px-6 rounded-full font-semibold transition-all ${
                        isPopular
                          ? "bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700 shadow-lg hover:shadow-xl"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-sky-100 dark:hover:bg-sky-900 hover:text-sky-600 dark:hover:text-sky-400"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pricingLoading && activePurchasePlan === plan.plan_id
                        ? "Processing..."
                        : isAuthenticated
                        ? "Purchase Credits"
                        : "Sign in to buy"}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-12 text-center text-slate-500 dark:text-slate-400 mb-16">
              Pricing plans are temporarily unavailable. Please try again shortly.
            </div>
          )}

          {/* Info Banner */}
          <div className="mb-16 flex items-center gap-4 rounded-2xl border-2 border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 p-6 text-slate-700 dark:text-slate-300">
            <ShieldCheck className="h-8 w-8 text-sky-600 dark:text-sky-400 flex-shrink-0" />
            <div>
              <p className="font-semibold mb-1">
                Start with {defaultCredits} free credits
              </p>
              <p className="text-sm">
                Credits never expire and cover single, bulk, and file validation
                workflows. No credit card required to start.
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-50 text-center mb-12">
              Frequently Asked Questions
            </h2>

            <div className="max-w-3xl mx-auto space-y-4">
              {FAQS.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-200 dark:border-slate-800 overflow-hidden hover:border-sky-300 dark:hover:border-sky-700 transition-all"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <div className="flex items-start gap-4">
                      <HelpCircle className="h-5 w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-1" />
                      <span className="text-lg font-bold text-slate-900 dark:text-slate-50 pr-4">
                        {faq.question}
                      </span>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-slate-600 dark:text-slate-400 flex-shrink-0 transition-transform ${
                        openFAQ === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFAQ === index ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <div className="px-6 pb-6 pl-16 text-slate-600 dark:text-slate-300 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
